/**
 * Upload templates — the library's "Upload" dialog.
 *
 * One step instead of two: the person picks the files AND says who the
 * templates are for (personal = private, project = public — anybody in the
 * project can use them), so a shared template no longer needs a second trip
 * to the lock icon. A `<stem>.placeholders.json` selected next to its file
 * sets that template's placeholders in the same call; the response's
 * coverage is surfaced as a warning when a defined placeholder is not
 * placed in the document.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Loader2, Lock, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { usePostRequest } from '@/shared/hooks/requests.js';
import { apiErrorMessage } from '@/shared/utils/apiError.js';
import { TEMPLATE_UPLOAD } from '@/shared/utils/urls.js';

// `.json` so a `<stem>.placeholders.json` can ride along with its template.
const ACCEPT = '.doc,.docx,.odt,.txt,.rtf,.html,.htm,.xlsx,.json';
const isSidecar = (file) => /\.placeholders\.json$/i.test(file?.name || '');

const OPTION = 'flex cursor-pointer items-start gap-2 rounded-[6px] border p-2 text-left';

export function UploadTemplatesDialog({ open, onOpenChange, onUploaded }) {
  const { t } = useTranslation(['my-docs']);
  const upload = usePostRequest({ url: TEMPLATE_UPLOAD, showError: false });
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [visibility, setVisibility] = useState('private');

  const templates = files.filter((f) => !isSidecar(f));
  const sidecars = files.length - templates.length;

  const close = () => { setFiles([]); onOpenChange(false); };

  const submit = async () => {
    if (!templates.length) return;
    const formData = new FormData();
    formData.append('visibility', visibility);
    files.forEach((file) => formData.append('uploaded_files', file));
    const { success, response, errorInfo } = await upload.request({ data: formData });
    if (!success) { toast.error(apiErrorMessage(errorInfo)); return; }
    const rows = Array.isArray(response) ? response : [];
    toast.success(t(visibility === 'public' ? 'my-docs:templates.uploadedPublic' : 'my-docs:templates.uploaded', { count: rows.length || templates.length }));
    // Defined-but-not-placed keys are the author's problem to know about now,
    // not when the first fill silently skips them.
    for (const row of rows) {
      const missing = row.placeholders?.missing || [];
      if (missing.length) {
        toast.warning(t('my-docs:templates.coverageMissing', { name: row.name, count: missing.length, keys: missing.join(', ') }));
      }
    }
    close();
    onUploaded?.(rows);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('my-docs:templates.uploadTitle')}</DialogTitle>
          <DialogDescription>{t('my-docs:templates.uploadHint')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-[4px] border border-dashed border-border bg-card px-3 text-sm text-foreground hover:bg-muted cursor-pointer"
          >
            <Upload className="h-4 w-4" />
            {templates.length
              ? t('my-docs:templates.filesChosen', { count: templates.length })
              : t('my-docs:templates.chooseFiles')}
          </button>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            data-testid="upload-templates-input"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          {templates.length > 0 && (
            <ul className="max-h-32 space-y-0.5 overflow-y-auto text-[11px] text-muted-foreground">
              {templates.map((f) => (
                <li key={f.name} className="truncate">{f.name}</li>
              ))}
            </ul>
          )}
          {sidecars > 0 && (
            <p className="text-[11px] text-muted-foreground">{t('my-docs:templates.sidecarsAttached', { count: sidecars })}</p>
          )}

          <fieldset className="space-y-1.5">
            <legend className="mb-1 text-xs font-medium text-foreground">{t('my-docs:templates.visibilityChoice')}</legend>
            <label className={`${OPTION} ${visibility === 'private' ? 'border-brand bg-brand/5' : 'border-border'}`}>
              <input type="radio" name="template-visibility" value="private" checked={visibility === 'private'} onChange={() => setVisibility('private')} className="mt-0.5" />
              <Lock className="mt-0.5 h-3.5 w-3.5 flex-none text-muted-foreground" />
              <span className="min-w-0">
                <span className="block text-sm text-foreground">{t('my-docs:templates.visibilityPersonal')}</span>
                <span className="block text-[11px] text-muted-foreground">{t('my-docs:templates.visibilityPersonalHint')}</span>
              </span>
            </label>
            <label className={`${OPTION} ${visibility === 'public' ? 'border-brand bg-brand/5' : 'border-border'}`}>
              <input type="radio" name="template-visibility" value="public" checked={visibility === 'public'} onChange={() => setVisibility('public')} className="mt-0.5" />
              <Globe className="mt-0.5 h-3.5 w-3.5 flex-none text-green-600" />
              <span className="min-w-0">
                <span className="block text-sm text-foreground">{t('my-docs:templates.visibilityProject')}</span>
                <span className="block text-[11px] text-muted-foreground">{t('my-docs:templates.visibilityProjectHint')}</span>
              </span>
            </label>
          </fieldset>
        </div>
        <DialogFooter>
          <button type="button" onClick={close} className="h-8 rounded-md border border-border bg-card px-3 text-xs font-medium text-foreground hover:bg-muted cursor-pointer">
            {t('my-docs:templates.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={upload.loading || !templates.length}
            className="inline-flex h-8 items-center gap-1 rounded-md bg-brand px-3 text-xs font-medium text-white disabled:opacity-50 cursor-pointer"
          >
            {upload.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {t('my-docs:templates.upload')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
